import { Form, Formik, type FormikHelpers, useField, useFormikContext } from "formik";
import {
  Activity,
  Eye,
  EyeOff,
  Globe,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  User,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Loader } from "@/components/common/loader";
import { PageHeader } from "@/components/common/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { isPlatformUser } from "@/features/auth/roles";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { type BpTeamMember, getBpTeam } from "@/features/business-practitioners/team-api";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { SelectField } from "@/features/settings/components/select-field";
import {
  type ClientVisibility,
  createMember,
  deactivateMember,
  listMembers,
  type Member,
  type MemberRole,
  resendInvite,
  ROLE_OPTIONS,
  updateMember,
  VISIBILITY_OPTIONS,
} from "@/features/team/api";
import { ApiError } from "@/lib/api";
import { applyApiErrors } from "@/lib/form-errors";
import { cn } from "@/lib/utils";

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS.map((o) => [o.value, o.label]));

/** The platform/org tier a member is badged by — GA/SA outrank the org roles. */
type Tier = "ga" | "sa" | "country_director" | "team_leader" | "staff";

function errMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

function initials(name: string, email: string) {
  const source = name.trim() || email;
  return (
    source
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

/** Platform identity wins over the org role for how we label/group a member. */
function tierOf(m: Member): Tier {
  if (m.isSuperuser) return "ga";
  if (m.isPlatformAdmin) return "sa";
  if (m.role === "country_director") return "country_director";
  if (m.role === "team_leader") return "team_leader";
  return "staff";
}

const TIER_LABEL: Record<Tier, string> = {
  ga: "Global Admin",
  sa: "Super Admin",
  country_director: "Country Director",
  team_leader: "Team Leader",
  staff: "Member",
};

/** Role badge styling, mapped onto Calm Studio tokens (navy / amber / blue / muted). */
const TIER_BADGE: Record<Tier, string> = {
  ga: "bg-brand-panel text-white",
  sa: "border border-brand-panel text-brand-panel bg-card",
  country_director: "bg-warning-soft text-warning",
  team_leader: "bg-accent text-info",
  staff: "bg-muted text-muted-foreground",
};

/** Avatar initials well, tinted to match the role badge. */
const TIER_AVATAR: Record<Tier, string> = {
  ga: "bg-brand-panel text-white",
  sa: "border-2 border-brand-panel text-brand-panel bg-card",
  country_director: "bg-warning-soft text-warning",
  team_leader: "bg-accent text-info",
  staff: "bg-muted text-muted-foreground",
};

/** A human label for what a member can reach — derived from role + visibility. */
function accessLabel(m: Member): string {
  switch (tierOf(m)) {
    case "ga":
      return "Everything, all countries";
    case "sa":
      return "Everything except billing";
    case "country_director":
      return "All org clients";
    case "team_leader":
      return "Team's clients";
    default:
      return m.clientVisibility === "assigned" ? "Assigned clients only" : "All clients";
  }
}

export function TeamTab() {
  const [list, setList] = useState<Member[] | null>(null);
  const [error, setError] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<Member | undefined>();
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "admins" | "directors" | "leaders" | "members">(
    "all",
  );
  const [statusFilter, setStatusFilter] = useState<"any" | Member["status"]>("any");

  const currentUserId = useAuthStore((s) => (s.user ? String(s.user.id) : null));
  const firmName = useAuthStore((s) => s.user?.firm?.name) ?? "your firm";
  // Who manages the roster: a platform admin (GA/SA — the only house users who
  // reach this page) or a firm's own Country Director (e.g. a BP firm's lead).
  const isAdmin = useAuthStore(
    (s) => isPlatformUser(s.user) || s.user?.role === "country_director",
  );
  // A Business-Practitioner firm also sees the NSI (house) members assigned to
  // it (read-only here — they're managed by the platform, not this firm).
  const bpFirm = useAuthStore((s) =>
    s.user?.firm?.org_type === "business_practitioner" ? s.user.firm : null,
  );
  const [nsiMembers, setNsiMembers] = useState<BpTeamMember[]>([]);

  useEffect(() => {
    let active = true;
    listMembers()
      .then((m) => active && setList(m))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!bpFirm) return;
    let active = true;
    getBpTeam(bpFirm.id)
      .then((t) => active && setNsiMembers(t.filter((m) => m.origin === "nsi_assigned")))
      .catch(() => {
        // The in-house list drives the error state; a failed NSI fetch just
        // hides that (read-only) section rather than breaking the whole tab.
      });
    return () => {
      active = false;
    };
  }, [bpFirm]);

  function upsert(saved: Member) {
    setList((prev) => {
      if (!prev) return [saved];
      const idx = prev.findIndex((m) => m.id === saved.id);
      if (idx === -1) return [...prev, saved];
      const next = [...prev];
      next[idx] = saved;
      return next;
    });
  }

  async function handleDeactivate(member: Member) {
    const wasInvited = member.status === "invited";
    try {
      await deactivateMember(member.id);
      setList(
        (prev) =>
          prev?.map((m) =>
            m.id === member.id ? { ...m, isActive: false, status: "deactivated" } : m,
          ) ?? null,
      );
      if (editingId === member.id) setEditingId(null);
      toast.success(
        wasInvited
          ? `Invitation to ${member.email} revoked`
          : `${member.name || member.email} deactivated`,
      );
    } catch (err) {
      toast.error(
        errMessage(
          err,
          wasInvited ? "Couldn't revoke this invite." : "Couldn't deactivate this member.",
        ),
      );
    } finally {
      setDeactivating(undefined);
    }
  }

  async function handleResend(member: Member) {
    if (resendingId) return; // guard against a double-fire re-sending the email
    setResendingId(member.id);
    try {
      upsert(await resendInvite(member.id));
      toast.success(`Invitation resent to ${member.email}`);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't resend this invite."));
    } finally {
      setResendingId(null);
    }
  }

  // Counts for the summary band + role hierarchy run off the whole roster
  // (excluding deactivated seats), independent of the roster's view filters.
  const seats = useMemo(() => (list ?? []).filter((m) => m.status !== "deactivated"), [list]);
  const counts = useMemo(() => {
    const by = (t: Tier) => seats.filter((m) => tierOf(m) === t).length;
    return {
      ga: by("ga"),
      sa: by("sa"),
      admins: by("ga") + by("sa"),
      directors: by("country_director"),
      leaders: by("team_leader"),
      members: by("staff"),
      invited: seats.filter((m) => m.status === "invited").length,
      people: seats.length,
    };
  }, [seats]);

  const editing = useMemo(
    () => (editingId ? (list?.find((m) => m.id === editingId) ?? null) : null),
    [editingId, list],
  );

  // Client-side roster filtering (the list endpoint already returns the firm).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (list ?? []).filter((m) => {
      if (q && !`${m.name} ${m.email}`.toLowerCase().includes(q)) return false;
      if (statusFilter !== "any" && m.status !== statusFilter) return false;
      const t = tierOf(m);
      if (roleFilter === "admins" && t !== "ga" && t !== "sa") return false;
      if (roleFilter === "directors" && t !== "country_director") return false;
      if (roleFilter === "leaders" && t !== "team_leader") return false;
      if (roleFilter === "members" && t !== "staff") return false;
      return true;
    });
  }, [list, search, statusFilter, roleFilter]);

  const leadership = filtered.filter((m) => {
    const t = tierOf(m);
    return t === "ga" || t === "sa" || t === "country_director";
  });
  const operations = filtered.filter((m) => {
    const t = tierOf(m);
    return t === "team_leader" || t === "staff";
  });

  // Pill counts run over the full roster (including deactivated) so they match
  // the rows the filter actually surfaces — unlike the summary band, which
  // counts active seats only (`seats`). Every member maps to exactly one tier,
  // so the four tiers sum to "All".
  const rosterByTier = (t: Tier) => (list ?? []).filter((m) => tierOf(m) === t).length;
  const rolePills: { key: typeof roleFilter; label: string; count: number }[] = [
    { key: "all", label: "All", count: list?.length ?? 0 },
    { key: "admins", label: "Admins", count: rosterByTier("ga") + rosterByTier("sa") },
    { key: "directors", label: "Directors", count: rosterByTier("country_director") },
    { key: "leaders", label: "Leaders", count: rosterByTier("team_leader") },
    { key: "members", label: "Members", count: rosterByTier("staff") },
  ];

  // Summary band reflects active team composition (excludes deactivated seats).
  const summary = [
    { label: "Admins", value: counts.admins },
    { label: "Country directors", value: counts.directors },
    { label: "Team leaders", value: counts.leaders },
    { label: "Members", value: counts.members },
    { label: "Pending", value: counts.invited, tone: counts.invited > 0 ? "warning" : undefined },
  ];

  return (
    <div className="flex flex-col gap-7">
      <PageHeader
        eyebrow="Workspace · People"
        title="Team"
        subtitle={
          <>
            Everyone who can sign in to {firmName}, their role and what they can reach —{" "}
            <strong className="text-foreground font-semibold">
              {counts.people} {counts.people === 1 ? "person" : "people"}
            </strong>
            {counts.invited > 0 && (
              <>
                ,{" "}
                <strong className="text-warning font-semibold">
                  {counts.invited} invite{counts.invited === 1 ? "" : "s"} pending
                </strong>
              </>
            )}
            . Seats are not capped.
          </>
        }
        actions={
          isAdmin && list ? (
            <Button type="button" variant="cta" className="gap-2" onClick={() => setInviteOpen(true)}>
              <UserPlus className="size-4" />
              Invite member
            </Button>
          ) : undefined
        }
      />

      {error ? (
        <p className="text-destructive text-sm">
          Couldn&apos;t load the team. Please refresh and try again.
        </p>
      ) : !list ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <>
          {/* Summary band */}
          <Card className="grid grid-cols-2 gap-0 overflow-hidden p-0 sm:grid-cols-3 lg:grid-cols-5">
            {summary.map((kpi, i) => (
              <div
                key={kpi.label}
                className={cn(
                  "flex flex-col gap-1.5 px-6 py-[1.125rem]",
                  i > 0 && "border-t sm:border-t-0",
                  i < summary.length - 1 && "lg:border-r",
                  i % 3 !== 2 && "sm:border-r",
                )}
              >
                <span className="text-muted-foreground text-xs font-semibold tracking-[0.08em] uppercase">
                  {kpi.label}
                </span>
                <span
                  className={cn(
                    "font-heading text-[1.625rem] leading-none font-semibold tabular-nums",
                    kpi.tone === "warning" && "text-warning",
                  )}
                >
                  {kpi.value}
                </span>
              </div>
            ))}
          </Card>

          {/* Body: roster + right rail */}
          <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-3">
            {/* Roster */}
            <Card className="gap-0 overflow-hidden p-0 xl:col-span-2">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-3 border-b p-4">
                <div className="relative min-w-0 flex-1 sm:max-w-xs">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                  <Input
                    type="search"
                    aria-label="Search team"
                    placeholder="Name or email…"
                    className="pl-9"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div
                  role="group"
                  aria-label="Filter by role"
                  className="bg-muted inline-flex flex-wrap gap-1 rounded-sm p-1"
                >
                  {rolePills.map((p) => {
                    const activePill = p.key === roleFilter;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        aria-pressed={activePill}
                        onClick={() => setRoleFilter(p.key)}
                        className={cn(
                          "inline-flex h-8 items-center gap-1.5 rounded-[10px] px-3 text-[13px] font-medium transition-colors",
                          activePill
                            ? "bg-card text-foreground font-semibold shadow-card"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {p.label}
                        {p.key === "all" && (
                          <span className="tabular-nums">· {p.count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="ml-auto">
                  <Select
                    value={statusFilter}
                    onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
                  >
                    <SelectTrigger size="sm" className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="invited">Invited</SelectItem>
                      <SelectItem value="deactivated">Deactivated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Person</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Access</TableHead>
                      <TableHead>Status</TableHead>
                      {isAdmin && (
                        <TableHead className="text-right">
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={isAdmin ? 5 : 4}
                          className="text-muted-foreground py-10 text-center text-sm"
                        >
                          No one matches these filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        <RosterGroup
                          label="Leadership"
                          members={leadership}
                          colSpan={isAdmin ? 5 : 4}
                          currentUserId={currentUserId}
                          isAdmin={isAdmin}
                          editingId={editingId}
                          resendingId={resendingId}
                          onEdit={setEditingId}
                          onResend={handleResend}
                          onDeactivate={setDeactivating}
                        />
                        <RosterGroup
                          label="Operations"
                          members={operations}
                          colSpan={isAdmin ? 5 : 4}
                          currentUserId={currentUserId}
                          isAdmin={isAdmin}
                          editingId={editingId}
                          resendingId={resendingId}
                          onEdit={setEditingId}
                          onResend={handleResend}
                          onDeactivate={setDeactivating}
                        />
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="text-muted-foreground border-t px-5 py-3 text-xs">
                Removing someone reassigns their clients first — nothing is deleted.
              </div>
            </Card>

            {/* Right rail */}
            <div className="flex flex-col gap-6">
              {isAdmin &&
                (editing ? (
                  <MemberEditPanel
                    key={editing.id}
                    member={editing}
                    isSelf={editing.id === currentUserId}
                    onClose={() => setEditingId(null)}
                    onSaved={upsert}
                    onDeactivate={() => setDeactivating(editing)}
                  />
                ) : (
                  <Card className="text-muted-foreground items-center gap-2 border-dashed px-6 py-10 text-center text-sm">
                    <Pencil className="size-5 opacity-60" />
                    Select a member to edit their role and access.
                  </Card>
                ))}

              <RoleHierarchyCard
                counts={{
                  ga: counts.ga,
                  sa: counts.sa,
                  country_director: counts.directors,
                  team_leader: counts.leaders,
                  staff: counts.members,
                }}
              />
            </div>
          </div>

          {bpFirm && nsiMembers.length > 0 && (
            <Card className="gap-0 overflow-hidden p-0">
              <div className="border-b p-4">
                <h3 className="text-foreground text-sm font-semibold">NSI-assigned members</h3>
                <p className="text-muted-foreground text-xs">
                  NSI (house) members assigned to your firm by the platform. Managed by NSI.
                </p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Home org</TableHead>
                      <TableHead>Origin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nsiMembers.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">
                          <span className="flex items-center gap-2">
                            {m.name || "—"}
                            {m.bp?.code && <Badge variant="outline">{m.bp.code}</Badge>}
                            {m.is_practitioner && <Badge variant="secondary">Practitioner</Badge>}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{m.email}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {m.home_org?.name ?? "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">NSI</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}

          <AddMemberDialog open={inviteOpen} onOpenChange={setInviteOpen} onAdded={upsert} />
          <AlertDialog
            open={!!deactivating}
            onOpenChange={(open) => !open && setDeactivating(undefined)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                {deactivating?.status === "invited" ? (
                  <>
                    <AlertDialogTitle>Revoke invitation for {deactivating?.email}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Their pending invitation is cancelled and the emailed link stops working. You
                      can invite them again later.
                    </AlertDialogDescription>
                  </>
                ) : (
                  <>
                    <AlertDialogTitle>
                      Deactivate {deactivating?.name || deactivating?.email}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      They&apos;ll immediately lose access. Any pending invitation is revoked. You
                      can re-invite them later.
                    </AlertDialogDescription>
                  </>
                )}
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deactivating && handleDeactivate(deactivating)}
                  className="bg-destructive text-white hover:bg-destructive/90"
                >
                  {deactivating?.status === "invited" ? "Revoke invitation" : "Deactivate"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

interface RosterGroupProps {
  label: string;
  members: Member[];
  colSpan: number;
  currentUserId: string | null;
  isAdmin: boolean;
  editingId: string | null;
  resendingId: string | null;
  onEdit: (id: string) => void;
  onResend: (m: Member) => void;
  onDeactivate: (m: Member) => void;
}

function RosterGroup({
  label,
  members,
  colSpan,
  currentUserId,
  isAdmin,
  editingId,
  resendingId,
  onEdit,
  onResend,
  onDeactivate,
}: RosterGroupProps) {
  if (members.length === 0) return null;
  return (
    <>
      <TableRow className="hover:bg-transparent">
        <TableCell
          colSpan={colSpan}
          className="text-muted-foreground bg-surface-hover py-2 text-[11px] font-semibold tracking-[0.1em] uppercase"
        >
          {label}
        </TableCell>
      </TableRow>
      {members.map((m) => {
        const t = tierOf(m);
        const isSelf = m.id === currentUserId;
        const selected = m.id === editingId;
        return (
          <TableRow key={m.id} className={cn(selected && "bg-surface-hover")}>
            <TableCell className={cn(selected && "border-info border-l-[3px] pl-[calc(1rem-3px)]")}>
              <span className="flex items-center gap-3">
                <span
                  className={cn(
                    "font-heading flex size-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold",
                    m.status === "invited" ? "border-input text-muted-foreground border border-dashed" : TIER_AVATAR[t],
                  )}
                >
                  {initials(m.name, m.email)}
                </span>
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="flex items-center gap-2 font-semibold">
                    {m.name || "—"}
                    {isSelf && (
                      <span className="bg-accent text-info rounded-full px-1.5 py-px text-[10px] font-bold tracking-[0.06em] uppercase">
                        You
                      </span>
                    )}
                    {m.isPractitioner && <Badge variant="secondary">Practitioner</Badge>}
                  </span>
                  <span className="text-muted-foreground text-xs">{m.email}</span>
                </span>
              </span>
            </TableCell>
            <TableCell>
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold tracking-[0.04em] uppercase",
                  TIER_BADGE[t],
                )}
              >
                {t === "ga" && <Activity className="size-3" />}
                {TIER_LABEL[t]}
              </span>
            </TableCell>
            <TableCell className="text-muted-foreground text-[13px]">{accessLabel(m)}</TableCell>
            <TableCell>
              <StatusPill status={m.status} />
            </TableCell>
            {isAdmin && (
              <TableCell className="text-right">
                <RowActions
                  member={m}
                  isSelf={isSelf}
                  selected={selected}
                  resending={resendingId === m.id}
                  onEdit={() => onEdit(m.id)}
                  onResend={() => onResend(m)}
                  onDeactivate={() => onDeactivate(m)}
                />
              </TableCell>
            )}
          </TableRow>
        );
      })}
    </>
  );
}

function StatusPill({ status }: { status: Member["status"] }) {
  if (status === "deactivated") {
    return (
      <span className="bg-muted text-muted-foreground inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold">
        <span className="bg-muted-foreground/60 size-1.5 rounded-full" />
        Deactivated
      </span>
    );
  }
  if (status === "invited") {
    return (
      <span className="bg-warning-soft text-warning inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold">
        <span className="bg-warning size-1.5 rounded-full" />
        Invited
      </span>
    );
  }
  return (
    <span className="bg-success-soft text-success inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold">
      <span className="bg-success size-1.5 rounded-full" />
      Active
    </span>
  );
}

interface RowActionsProps {
  member: Member;
  isSelf: boolean;
  selected: boolean;
  resending: boolean;
  onEdit: () => void;
  onResend: () => void;
  onDeactivate: () => void;
}

function RowActions({
  member,
  isSelf,
  selected,
  resending,
  onEdit,
  onResend,
  onDeactivate,
}: RowActionsProps) {
  // A member's own row never exposes destructive/role actions.
  if (isSelf) return <span className="text-muted-foreground text-xs">—</span>;

  if (member.status === "invited") {
    return (
      <div className="flex items-center justify-end gap-1.5">
        <Button type="button" size="sm" variant="outline" disabled={resending} onClick={onResend}>
          Resend
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label={`Revoke invitation for ${member.email}`}
          onClick={onDeactivate}
        >
          <X className="size-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        type="button"
        size="icon-sm"
        variant="ghost"
        aria-label={`Edit ${member.name || member.email}`}
        aria-pressed={selected}
        className={cn(selected && "bg-accent text-info")}
        onClick={onEdit}
      >
        <Pencil className="size-4" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" size="icon-sm" variant="ghost" aria-label="Member actions">
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onEdit}>Edit member</DropdownMenuItem>
          {member.status !== "deactivated" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={onDeactivate}>
                Deactivate
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const ROLE_HINTS: Record<MemberRole, string> = {
  country_director: "Oversees all clients and practitioners · approves allocations.",
  team_leader: "Leads a team of members · can assign their clients and approve documents.",
  staff: "Works assigned clients only · documents, notes, chat.",
};

const editSchema = Yup.object({
  role: Yup.string().required(),
  clientVisibility: Yup.string().oneOf(["all", "assigned"]).required(),
  isActive: Yup.boolean().required(),
  // Optional reset — both blank keeps the current password; once either field
  // is touched they must be ≥8 and match (so a stray confirm-only entry errors
  // instead of being silently dropped).
  newPassword: Yup.string().test("min", "At least 8 characters", (v) => !v || v.length >= 8),
  confirmPassword: Yup.string().test("match", "Passwords must match", function (value) {
    const { newPassword } = this.parent as { newPassword?: string };
    if (!newPassword && !value) return true;
    return value === newPassword;
  }),
});

interface EditValues {
  role: MemberRole;
  clientVisibility: ClientVisibility;
  isActive: boolean;
  newPassword: string;
  confirmPassword: string;
}

/** Inline right-rail editor (replaces the old modal). Reuses updateMember. */
function MemberEditPanel({
  member,
  isSelf,
  onClose,
  onSaved,
  onDeactivate,
}: {
  member: Member;
  isSelf: boolean;
  onClose: () => void;
  onSaved: (m: Member) => void;
  onDeactivate: () => void;
}) {
  const t = tierOf(member);

  async function handleSubmit(values: EditValues, { setErrors }: FormikHelpers<EditValues>) {
    try {
      onSaved(
        await updateMember(member.id, {
          role: values.role,
          clientVisibility: values.clientVisibility,
          isActive: values.isActive,
          password: values.newPassword || undefined,
        }),
      );
      toast.success("Member updated");
      // Close on success (mirrors the old modal) — also unmounts the form so any
      // just-submitted password value never lingers in component state.
      onClose();
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't update this member." }));
    }
  }

  return (
    <Card className="border-info ring-info/15 gap-0 overflow-hidden p-0 ring-1">
      <div className="flex items-center justify-between gap-3 border-b p-4">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={cn(
              "font-heading flex size-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold",
              TIER_AVATAR[t],
            )}
          >
            {initials(member.name, member.email)}
          </span>
          <div className="flex min-w-0 flex-col leading-tight">
            <h2 className="font-heading text-[17px] font-semibold tracking-[-0.01em]">
              Edit member
            </h2>
            <span className="text-muted-foreground truncate text-xs">
              {member.name || member.email}
            </span>
          </div>
        </div>
        <Button type="button" size="icon-sm" variant="ghost" aria-label="Close" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>

      <Formik<EditValues>
        initialValues={{
          role: member.role ?? "staff",
          clientVisibility: member.clientVisibility,
          isActive: member.isActive,
          newPassword: "",
          confirmPassword: "",
        }}
        validationSchema={editSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values }) => (
          <Form>
            <div className="flex flex-col gap-5 p-5">
              <div className="grid gap-1.5">
                <Label className="text-muted-foreground text-xs font-semibold tracking-[0.06em] uppercase">
                  Full name
                </Label>
                <Input value={member.name || "—"} disabled />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-muted-foreground text-xs font-semibold tracking-[0.06em] uppercase">
                  Work email
                </Label>
                <Input value={member.email} disabled />
              </div>
              <div>
                <SelectField
                  name="role"
                  label="Role"
                  options={ROLE_OPTIONS}
                  labelClassName="text-muted-foreground text-xs font-semibold tracking-[0.06em] uppercase"
                />
                <p className="text-muted-foreground mt-1.5 text-xs">{ROLE_HINTS[values.role]}</p>
              </div>
              <ClientVisibilityField name="clientVisibility" />
              <StatusField name="isActive" />
              <div className="flex flex-col gap-3 border-t pt-4">
                <p className="text-muted-foreground text-xs font-semibold tracking-[0.06em] uppercase">
                  Reset password (optional)
                </p>
                <PasswordField name="newPassword" label="New password" />
                <PasswordField name="confirmPassword" label="Confirm new password" />
                <p className="text-muted-foreground text-xs">
                  Leave blank to keep their current password.
                </p>
              </div>
            </div>
            <div className="bg-surface-hover flex flex-wrap items-center justify-between gap-2 border-t p-4">
              {member.isActive && !isSelf ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  onClick={onDeactivate}
                >
                  Deactivate
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Card>
  );
}

const ROLE_HIERARCHY: {
  tier: Tier;
  label: string;
  desc: string;
  icon: ReactNode;
  tone: string;
  indent: string;
}[] = [
  {
    tier: "ga",
    label: "Global admin",
    desc: "Owns the workspace, billing, every country",
    icon: <Activity className="size-4" />,
    tone: "bg-brand-panel text-white",
    indent: "",
  },
  {
    tier: "sa",
    label: "Super admin",
    desc: "All operations, settings and people — no billing",
    icon: <ShieldCheck className="size-4" />,
    tone: "border-2 border-brand-panel text-brand-panel bg-card",
    indent: "pl-3",
  },
  {
    tier: "country_director",
    label: "Country director",
    desc: "Every client, practitioner and report in one country",
    icon: <Globe className="size-4" />,
    tone: "bg-warning-soft text-warning",
    indent: "pl-6",
  },
  {
    tier: "team_leader",
    label: "Team leader",
    desc: "Their team's clients · assigns work, approves letters",
    icon: <Users className="size-4" />,
    tone: "bg-accent text-info",
    indent: "pl-9",
  },
  {
    tier: "staff",
    label: "Member",
    desc: "Assigned clients only · documents, notes, chat",
    icon: <User className="size-4" />,
    tone: "bg-muted text-muted-foreground",
    indent: "pl-12",
  },
];

function RoleHierarchyCard({ counts }: { counts: Record<Tier, number> }) {
  return (
    <Card className="gap-1 p-4">
      <h2 className="font-heading px-2 pb-1 text-[17px] font-semibold tracking-[-0.01em]">
        Role hierarchy
      </h2>
      {ROLE_HIERARCHY.map((r) => (
        <div key={r.tier} className={cn("flex items-center gap-3 rounded-sm p-2.5", r.indent)}>
          <span
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-sm",
              r.tone,
            )}
          >
            {r.icon}
          </span>
          <span className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-[13px] font-semibold">{r.label}</span>
            <span className="text-muted-foreground text-xs">{r.desc}</span>
          </span>
          <span className="text-muted-foreground shrink-0 text-xs font-semibold tabular-nums">
            {counts[r.tier]}
          </span>
        </div>
      ))}
    </Card>
  );
}

/** "Show All Clients" / "Show only assigned clients" radio, Formik-bound. */
function ClientVisibilityField({ name }: { name: string }) {
  const [field, , helpers] = useField<ClientVisibility>(name);
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground text-xs font-semibold tracking-[0.06em] uppercase">
        Client access
      </Label>
      <RadioGroup
        value={field.value}
        onValueChange={(v) => helpers.setValue(v as ClientVisibility)}
        className="gap-1.5"
      >
        {VISIBILITY_OPTIONS.map((o) => {
          const active = field.value === o.value;
          return (
            <Label
              key={o.value}
              htmlFor={`${name}-${o.value}`}
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-[11px] border px-3 py-2.5 text-[13px] font-normal",
                active ? "border-info bg-surface-hover" : "border-border",
              )}
            >
              <RadioGroupItem value={o.value} id={`${name}-${o.value}`} />
              {o.label}
            </Label>
          );
        })}
      </RadioGroup>
      <p className="text-muted-foreground text-xs">
        Administrators always see all clients regardless of this setting.
      </p>
    </div>
  );
}

/** Formik-bound password input with a show/hide toggle (mirrors the auth forms). */
function PasswordField({
  name,
  label,
  autoComplete = "new-password",
}: {
  name: string;
  label: string;
  autoComplete?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <FormField
      name={name}
      label={label}
      type={show ? "text" : "password"}
      autoComplete={autoComplete}
      placeholder="••••••••"
      endAdornment={
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
        </button>
      }
    />
  );
}

/** Active / Inactive radio, Formik-bound to a boolean field. */
function StatusField({ name }: { name: string }) {
  const [field, meta, helpers] = useField<boolean>(name);
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground text-xs font-semibold tracking-[0.06em] uppercase">
        Status
      </Label>
      <RadioGroup
        value={field.value ? "active" : "inactive"}
        onValueChange={(v) => helpers.setValue(v === "active")}
        className="flex-row gap-4 pt-0.5"
      >
        {[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ].map((o) => (
          <div key={o.value} className="flex items-center gap-2">
            <RadioGroupItem value={o.value} id={`${name}-${o.value}`} />
            <Label htmlFor={`${name}-${o.value}`} className="font-normal">
              {o.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
      {hasError ? (
        <p className="text-destructive text-xs">{meta.error}</p>
      ) : (
        <p className="text-muted-foreground text-xs">Inactive members can&apos;t sign in.</p>
      )}
    </div>
  );
}

const addSchema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
  email: Yup.string().email("Enter a valid email").required("Email is required"),
  password: Yup.string().min(8, "At least 8 characters").required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm the password"),
  role: Yup.string().required(),
  clientVisibility: Yup.string().oneOf(["all", "assigned"]).required(),
  isPractitioner: Yup.boolean(),
});

interface AddValues {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: MemberRole;
  clientVisibility: ClientVisibility;
  isPractitioner: boolean;
}

/** The "is also a practitioner" checkbox only renders for a Country Director.
 * The submit handler further guards the payload so a stale toggle from before a
 * role switch can't leak through. */
function IsPractitionerField() {
  const { values } = useFormikContext<AddValues>();
  if (values.role !== "country_director") return null;
  return (
    <CheckboxField
      name="isPractitioner"
      label="Is also a practitioner"
      hint="Also create a linked practitioner record so they appear in the practitioner list."
    />
  );
}

/** The "Add a user" dialog — exported so the onboarding "Invite your team" step
 * can reuse the exact same create-member flow. */
export function AddMemberDialog({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: (m: Member) => void;
}) {
  async function handleSubmit(values: AddValues, { setErrors }: FormikHelpers<AddValues>) {
    try {
      const member = await createMember({
        name: values.name,
        email: values.email,
        password: values.password,
        role: values.role,
        clientVisibility: values.clientVisibility,
        // Only a Country Director can also be a practitioner; never send true
        // for any other role (matches the server-side rule).
        isPractitioner: values.role === "country_director" && values.isPractitioner,
      });
      onAdded(member);
      onOpenChange(false);
      toast.success(`${values.name} added`);
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't add this member." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a user</DialogTitle>
          <DialogDescription>
            They can sign in right away with the email and password you set.
          </DialogDescription>
        </DialogHeader>
        <Formik<AddValues>
          initialValues={{
            name: "",
            email: "",
            password: "",
            confirmPassword: "",
            role: "staff",
            clientVisibility: "all",
            isPractitioner: false,
          }}
          validationSchema={addSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField name="name" label="Name" />
              <FormField name="email" label="Email" type="email" />
              <PasswordField name="password" label="Password" />
              <PasswordField name="confirmPassword" label="Confirm password" />
              <SelectField name="role" label="Role" options={ROLE_OPTIONS} />
              <IsPractitionerField />
              <ClientVisibilityField name="clientVisibility" />
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Adding…" : "Add user"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
